import {
  type DefaultError,
  MutationCache,
  type MutationMeta,
  QueryCache,
  QueryClient,
  type QueryKey,
  type QueryMeta,
} from '@tanstack/react-query';
import { isRedirect, type AnyRouter } from '@tanstack/react-router';
import { type AuthContext } from '@/helpers/auth';
import { ONE_HOUR_MILLISECONDS } from '@/utils/constants';
import {
  isUnauthorizedError,
  mutationRetryHandler,
  queryClientErrorHandler,
  queryRetryHandler,
  withSentryContext,
} from './errors';
import { deriveSentryContext, mergeSentryContexts, type CaptureSurface } from './sentry-context';
import { getAuthRefreshTokenQueryOptions, getTokenFreshness } from './services/auth';

/**
 * Creates and configures the TanStack Query client for the application.
 * The QueryClient integrates with TanStack Router for seamless error handling and redirects.
 */
export function createQueryClient(router: AnyRouter, auth: AuthContext): QueryClient {
  const errorHandler = queryClientErrorHandler(router, { ssoLoginHint: auth.user?.email });
  const onErrorWithMeta = (
    err: DefaultError,
    meta: QueryMeta | MutationMeta | undefined,
    surface: CaptureSurface,
    queryKey: QueryKey | undefined,
  ) => {
    if (isRedirect(err)) {
      return errorHandler(err);
    }

    const derived = deriveSentryContext(err, surface, queryKey, router);

    // The backend rejecting a token the client believed was fresh is the only evidence available on
    // whether the staleness leeway is right, and it is readable only against that token's own age at
    // the moment the request failed.
    if (isUnauthorizedError(err)) {
      derived.extra = { ...derived.extra, ...getTokenFreshness(queryClient) };
    }

    return errorHandler(withSentryContext(err, mergeSentryContexts(derived, meta?.sentryContext), meta?.sentryFilter));
  };

  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (err, query) => onErrorWithMeta(err, query.meta, 'query', query.queryKey),
    }),
    mutationCache: new MutationCache({
      onError: (err, _variables, _context, mutation) =>
        onErrorWithMeta(err, mutation.meta, 'mutation', mutation.options.mutationKey),
    }),
    defaultOptions: {
      queries: {
        staleTime: ONE_HOUR_MILLISECONDS / 2,
        retry: queryRetryHandler,
      },
      mutations: {
        retry: mutationRetryHandler,
      },
    },
  });
  const { queryKey, ...queryOptions } = getAuthRefreshTokenQueryOptions(auth, router);

  queryClient.setQueryDefaults(queryKey, queryOptions);

  return queryClient;
}
